import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  Money,
  ReferenceModel,
} from "../../base/index.js";
import type { AsyncResourceVariant } from "../../react/index.js";
import { provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound.js";
import type {
  ContractData,
  ContractListItemData,
  ContractListQueryData,
  ContractTerminationCreateRequestData,
} from "./types.js";
import { ContractTermination } from "../ContractTermination/index.js";
import { Customer } from "../../customer/index.js";
import { AdditionalItem } from "./AdditionalItem.js";
import { ContractArticle } from "../ContractArticle/index.js";
import { DateTime } from "luxon";
import { TariffChange } from "../TariffChange/index.js";
import { ContractItemReference } from "../ContractItem/ContractItemReference.js";

export class Contract extends ReferenceModel {
  public static ofId(id: string) {
    return new Contract(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.contract.find(id);
    if (data !== undefined) {
      return new ContractDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const contract = await this.find(id);
    assertObjectFound(contract, "contract", id);
    return contract;
  });

  public static findByProject = provideReact(async (projectId: string) => {
    const data = await config.behaviors.contract.findByProject(projectId);
    if (data !== undefined) {
      return new ContractDetailed(data);
    }
  });

  public static getByProject = provideReact(async (projectId: string) => {
    const contract = await this.findByProject(projectId);
    assertObjectFound(contract, "contract", projectId);
    return contract;
  });

  public static findByServer = provideReact(async (serverId: string) => {
    const data = await config.behaviors.contract.findByServer(serverId);
    if (data !== undefined) {
      return new ContractDetailed(data);
    }
  });

  public static getByServer = provideReact(async (serverId: string) => {
    const contract = await this.findByServer(serverId);
    assertObjectFound(contract, "contract", serverId);
    return contract;
  });

  public static query(customer: Customer, query: ContractListQueryData = {}) {
    return new ContractListQuery(customer, query);
  }

  public getDetailed = provideReact(() => {
    return Contract.get(this.id);
  }, [this.id]) as AsyncResourceVariant<() => Promise<ContractDetailed>>;

  public findDetailed = provideReact(() => {
    return Contract.get(this.id);
  }, [this.id]) as AsyncResourceVariant<
    () => Promise<ContractDetailed | undefined>
  >;

  public async terminate(data: ContractTerminationCreateRequestData) {
    await config.behaviors.contract.terminate(this.id, data);
  }

  public async cancelTermination() {
    await config.behaviors.contract.cancelTermination(this.id);
  }
}

class ContractCommon extends classes(
  DataModel<ContractListItemData | ContractData>,
  Contract,
) {
  public readonly termination?: ContractTermination;
  public readonly additionalItems: AdditionalItem[];
  public readonly additionalItemsTotalPrice: Money;
  public readonly domainItemsCount: number;
  public readonly customer: Customer;
  public readonly baseItemId: string;
  public readonly baseItemTotalPrice: Money;
  public readonly tariff?: string;
  public readonly tariffChange?: TariffChange;
  public readonly description: string;
  public readonly baseItemArticles: ContractArticle[];
  public readonly nextPossibleDowngradeDate?: DateTime;
  public readonly nextPossibleUpgradeDate?: DateTime;
  public readonly nextPossibleTerminationDate?: DateTime;
  public readonly aggregateReference?: ContractItemReference;
  public readonly activationDate?: DateTime;
  public readonly freeTrialUntil?: DateTime;
  public readonly freeTrialDaysRemaining?: number;
  public readonly contractPeriod: number;
  public readonly articleName: string;
  public readonly hostingDescription?: string;
  public readonly hostingShortId?: string;

  public constructor(data: ContractListItemData | ContractData) {
    super([data], [data.contractId]);
    if (data.termination) {
      this.termination = new ContractTermination(data.termination);
    }
    this.customer = Customer.ofId(data.customerId);
    this.additionalItems = data.additionalItems
      ? data.additionalItems.map((i) => new AdditionalItem(i))
      : [];
    this.additionalItemsTotalPrice = this.additionalItems.reduce(
      (total, item) => {
        return total.add(item.totalPrice);
      },
      Money({ amount: 0, currency: "EUR" }),
    );
    this.domainItemsCount = this.additionalItems.filter((i) =>
      i.articleName.toLowerCase().includes("domain"),
    ).length;
    this.baseItemId = data.baseItem.itemId;
    this.tariff = data.baseItem.articles[0]?.name;
    if (data.baseItem.tariffChange) {
      this.tariffChange = new TariffChange(data.baseItem.tariffChange);
    }
    this.description = data.baseItem.description;
    this.hostingDescription = /"(.*?)"/.exec(this.description)?.[1];
    const descriptionParts = this.description.split(":")[0]?.split(" ");
    if (descriptionParts) {
      this.hostingShortId = descriptionParts[descriptionParts.length - 1];
    }
    this.baseItemArticles = data.baseItem.articles.map((articleData) => {
      return new ContractArticle(articleData, this.baseItemId);
    });
    if (data.baseItem.nextPossibleDowngradeDate) {
      this.nextPossibleDowngradeDate = DateTime.fromISO(
        data.baseItem.nextPossibleDowngradeDate,
      );
    }
    if (data.baseItem.nextPossibleUpgradeDate) {
      this.nextPossibleUpgradeDate = DateTime.fromISO(
        data.baseItem.nextPossibleUpgradeDate,
      );
    }
    if (data.baseItem.nextPossibleTerminationDate) {
      this.nextPossibleTerminationDate = DateTime.fromISO(
        data.baseItem.nextPossibleTerminationDate,
      );
    }

    this.aggregateReference = data.baseItem.aggregateReference
      ? new ContractItemReference(data.baseItem.aggregateReference)
      : undefined;
    if (data.baseItem.activationDate) {
      this.activationDate = DateTime.fromISO(data.baseItem.activationDate);
    }
    this.freeTrialUntil =
      data.baseItem.isInFreeTrial &&
      this.activationDate &&
      this.activationDate.diff(DateTime.now()).toMillis() > 0
        ? this.activationDate
        : undefined;
    if (this.freeTrialUntil) {
      this.freeTrialDaysRemaining = Math.ceil(
        this.freeTrialUntil.diff(DateTime.now(), ["days"]).days,
      );
    }
    this.contractPeriod = data.baseItem.contractPeriod;
    this.articleName = this.baseItemArticles[0]?.name ?? "";
    this.baseItemTotalPrice = Money({
      amount: data.baseItem.totalPrice.value,
      currency: "EUR",
    });
  }

  public async cancelTariffChange() {
    await config.behaviors.contract.cancelTariffChange(
      this.id,
      this.baseItemId,
    );
  }
}

export class ContractDetailed extends classes(
  ContractCommon,
  DataModel<ContractData>,
) {
  public constructor(data: ContractData) {
    super([data], [data]);
  }
}

export class ContractListItem extends classes(
  ContractCommon,
  DataModel<ContractListItemData>,
) {
  public constructor(data: ContractListItemData) {
    super([data], [data]);
  }
}

export class ContractListQuery extends ListQueryModel<ContractListQueryData> {
  public readonly customer: Customer;
  public constructor(customer: Customer, query: ContractListQueryData) {
    super(query, {
      dependencies: [customer.id],
    });
    this.customer = customer;
  }

  public refine(query: ContractListQueryData) {
    return new ContractListQuery(this.customer, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items, totalCount } = await config.behaviors.contract.list(
      this.customer.id,
      this.query,
    );

    return new ContractList(
      this.customer,
      this.query,
      items.map((d) => new ContractListItem(d)),
      totalCount,
    );
  }, [this.queryId, "contract"]);

  public getTotalCount = provideReact(async () => {
    const { totalCount } = await this.refine({ limit: 1 }).execute();
    return totalCount;
  }, [this.queryId, "contract"]);
}

export class ContractList extends classes(
  ContractListQuery,
  ListDataModel<ContractListItem>,
) {
  public constructor(
    customer: Customer,
    query: ContractListQueryData,
    contracts: ContractListItem[],
    totalCount: number,
  ) {
    super([customer, query], [contracts, totalCount]);
  }
}
